// Site zones (CR_20260914) — the rules web and mobile share: how an asset's zone is shown (§4.4), chip counts and
// the zone filter, history texts, who may move assets, and the zone sign address.
import type { AssetOverview, SiteZone, Whoami, ZoneMove } from './types';

// A zone is a person's claim ("last put in Bay A"), tied to the custody site. It is shown plainly while the asset
// is at that site (or can't be sensed: QR only / never seen), dimmed as "last: Bay A" while it is away (in transit,
// off site, missing, on site elsewhere — decision Z7), and not at all on another site's board.

export interface ZoneFields {
  zone_id: string | null;
  zone_name: string | null;
  zone_set_at: string | null;
  zone_set_by_name: string | null;
  custody_site_id: string | null;
  presence_state: string | null;
  presence_site_id: string | null;
  has_electronic_tag: boolean;
}

export type ZoneDisplay =
  | { kind: 'none' }
  | { kind: 'here' | 'last'; name: string; setAt: string | null; setBy: string | null };

/** `siteId`: the board being looked at; omit on an asset's own page. */
export function zoneDisplay(a: ZoneFields, siteId?: string): ZoneDisplay {
  if (!a.zone_id || !a.zone_name) return { kind: 'none' };
  if (siteId !== undefined && a.custody_site_id !== siteId) return { kind: 'none' };
  const here =
    !a.has_electronic_tag ||
    !a.presence_state ||
    a.presence_state === 'unknown' ||
    (a.presence_state === 'on_site' && a.presence_site_id === a.custody_site_id);
  return {
    kind: here ? 'here' : 'last',
    name: a.zone_name,
    setAt: a.zone_set_at,
    setBy: a.zone_set_by_name,
  };
}

/** Zone sign address (decision Z10): the zone id, so renaming a zone never invalidates a printed sign. */
export function zoneLabelUrl(publicUrl: string, companyCode: string, zoneId: string): string {
  return `${publicUrl.replace(/\/+$/, '')}/z/${encodeURIComponent(companyCode)}/${encodeURIComponent(zoneId)}`;
}

export const NO_ZONE = 'none';

/** Chip counts: every row, each zone's assets in custody here, and custody-here assets in no zone. */
export function zoneCounts(
  rows: AssetOverview[],
  siteId: string,
): { all: number; none: number; byZone: Map<string, number> } {
  const byZone = new Map<string, number>();
  let none = 0;
  for (const a of rows) {
    if (a.custody_site_id !== siteId) continue;
    if (a.zone_id) byZone.set(a.zone_id, (byZone.get(a.zone_id) ?? 0) + 1);
    else none++;
  }
  return { all: rows.length, none, byZone };
}

/** `filter`: '' every row · 'none' custody here without a zone · a zone id. */
export function inZone(a: AssetOverview, siteId: string, filter: string): boolean {
  if (!filter) return true;
  if (a.custody_site_id !== siteId) return false;
  return filter === NO_ZONE ? !a.zone_id : a.zone_id === filter;
}

/** The i18n key and values for one history row ("Moved to Bay B", "Left Bay A (moved to another site)" …). */
export function zoneMoveText(m: Pick<ZoneMove, 'reason' | 'from_zone' | 'to_zone'>): {
  key: string;
  values: { from: string; to: string };
} {
  const values = { from: m.from_zone?.name ?? '—', to: m.to_zone?.name ?? '—' };
  if (m.reason === 'moved' && !m.to_zone) return { key: 'zone_event.removed', values };
  return { key: `zone_event.${m.reason}`, values };
}

/** Same rule as app.can_act_at_site: managers, all-sites users and the site's own selected-sites users. */
export function canActAt(me: Whoami | undefined, siteId: string | null | undefined): boolean {
  if (!me?.company || !siteId) return false;
  return (
    me.role === 'manager' ||
    me.platform_admin ||
    me.site_access === 'all' ||
    me.site_ids.includes(siteId)
  );
}

/** Zones a picker offers: active ones, plus the current zone even if archived (so the select shows it). */
export function pickableZones(zones: SiteZone[] | undefined, current?: string | null): SiteZone[] {
  return (zones ?? []).filter((z) => z.is_active || z.id === current);
}

/**
 * Put-away mode (§4.5): what scanning this asset under this zone's sign does. Only `move` sends a command;
 * an arrival still in another site's custody must be received first (a partial receive would dispute the rest).
 */
export type PutAwayOutcome = 'move' | 'already' | 'awaiting' | 'elsewhere' | 'not_active';

export function putAwayOutcome(
  a: Pick<
    AssetOverview,
    'asset_status' | 'custody_site_id' | 'zone_id' | 'open_transfer_to_site_id'
  >,
  zone: Pick<SiteZone, 'id' | 'site_id'>,
): PutAwayOutcome {
  if (a.asset_status !== 'active') return 'not_active';
  if (a.custody_site_id !== zone.site_id)
    return a.open_transfer_to_site_id === zone.site_id ? 'awaiting' : 'elsewhere';
  return a.zone_id === zone.id ? 'already' : 'move';
}
